import React, { useState, useEffect } from 'react';
import { Trash2, Save, Plus } from 'lucide-react';
import { taskService, TaskResourceActual, TaskResourceActualCreate, Task } from '../../../services/taskService';
import FormControl from '../../../components/common/FormControl';
import Modal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import Toast, { ToastMessage } from '../../../components/common/Toast';
import { confirmDelete } from '../../../components/common/confirm';
import Skeleton from '../../../components/common/Skeleton';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput, ValidatedDatePicker } from '../../../validation/ValidatedFields';

type BookingView = 'LIST' | 'BY_RESOURCE';

const isStaffType = (t: string) => t === 'STAFF' || t === 'MEN';
const typeBadge = (t: string) =>
    `status-badge xs ${isStaffType(t) ? 'info' : 'warning'}`;
const typeLabel = (t: string) => (isStaffType(t) ? 'STAFF' : t);

/**
 * Reusable Book-Actuals panel. Used standalone (ActualBookingModal) AND as the
 * Booking tab inside the Edit Task modal, so a task is a single point of reference.
 *
 * Listing is pure: adding happens in a modal. Two views — a granular
 * chronological list, and a per-resource aggregate (budget vs actual vs variance).
 */
export const ActualBookingPanel: React.FC<{ task: Task; onChanged?: () => void }> = ({ task, onChanged }) => {
    const [actuals, setActuals] = useState<TaskResourceActual[]>([]);
    const [budgetedResources, setBudgetedResources] = useState<any[]>([]);
    const [view, setView] = useState<BookingView>('BY_RESOURCE');
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [newActual, setNewActual] = useState<TaskResourceActualCreate>({
        resource_type: 'STAFF', resource_id: 0,
        actual_date: new Date().toISOString().split('T')[0], actual_hours: 0,
    });
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const vh = useFieldValidation();

    // One combined picker: every resource allocated to this task, any kind.
    const resourceOptions = budgetedResources.map(r => ({
        value: `${r.resource_type}:${r.resource_id}`,
        label: `${typeLabel(r.resource_type)} · ${r.resource_name} (Budgeted ${r.quantity}h @ $${r.rate}/h)`,
    }));

    // Granular list, latest booking first.
    const chronological = [...actuals].sort((a, b) =>
        (b.actual_date || '').localeCompare(a.actual_date || ''));

    // Aggregate per resource: budget (from the allocation) vs actual (summed
    // bookings) vs variance. Seeded from allocations so a resource with a
    // budget but no bookings still shows.
    const aggregate = (() => {
        const map = new Map<string, {
            key: string; name: string; type: string;
            budgetHrs: number; budgetCost: number;
            actualHrs: number; actualCost: number;
        }>();
        budgetedResources.forEach(r => {
            const key = `${r.resource_type}:${r.resource_id}`;
            map.set(key, {
                key, name: r.resource_name, type: r.resource_type,
                budgetHrs: Number(r.quantity) || 0, budgetCost: Number(r.total_cost) || 0,
                actualHrs: 0, actualCost: 0,
            });
        });
        actuals.forEach(a => {
            const key = `${a.resource_type}:${a.resource_id}`;
            let row = map.get(key);
            if (!row) {
                row = {
                    key, name: a.resource_name || '—', type: a.resource_type,
                    budgetHrs: 0, budgetCost: 0, actualHrs: 0, actualCost: 0,
                };
                map.set(key, row);
            }
            row.actualHrs += Number(a.actual_hours) || 0;
            row.actualCost += Number(a.total_cost) || 0;
        });
        return Array.from(map.values());
    })();

    const totalActual = actuals.reduce((s, a) => s + a.total_cost, 0);
    const totalBudget = budgetedResources.reduce((s, r) => s + (Number(r.total_cost) || 0), 0);

    useEffect(() => {
        if (task) { vh.reset(); loadData(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task?.id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [act, res] = await Promise.all([
                taskService.getTaskResourceActuals(task.id),
                taskService.getTaskResources(task.id),
            ]);
            setActuals(act);
            setBudgetedResources(res);
        } catch {
            setToast({ type: 'error', message: 'Failed to load data.' });
        } finally {
            setLoading(false);
        }
    };

    const openAdd = () => {
        setNewActual({
            resource_type: 'STAFF', resource_id: 0,
            actual_date: new Date().toISOString().split('T')[0], actual_hours: 0,
        });
        vh.reset();
        setIsAddOpen(true);
    };

    const handleAddActual = async () => {
        if (!task || !newActual.resource_id) return;
        const ok = vh.validateAll([
            { name: 'actual_date', code: 305, value: newActual.actual_date, required: true },
            { name: 'actual_hours', code: 206, value: newActual.actual_hours, required: true },
        ]);
        if (!ok) return;
        setBooking(true);
        try {
            await taskService.createTaskResourceActual(task.id, newActual);
            await loadData();
            setIsAddOpen(false);
            vh.reset();
            onChanged?.();
            setToast({ type: 'success', message: 'Actual booked successfully.' });
        } catch (err: any) {
            setToast({ type: 'error', message: `Failed to add actual: ${err.response?.data?.detail || err.message}` });
        } finally {
            setBooking(false);
        }
    };

    const handleDeleteActual = async (actualId: number) => {
        if (!(await confirmDelete({ title: 'Remove Booking?', text: 'This booking will be removed.', confirmText: 'Yes, Remove' }))) return;
        try {
            await taskService.deleteTaskResourceActual(actualId);
            await loadData();
            onChanged?.();
            setToast({ type: 'delete', message: 'Booking removed.' });
        } catch {
            setToast({ type: 'error', message: 'Failed to delete booking.' });
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Toolbar: view toggle + Add */}
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                    {([['LIST', 'List'], ['BY_RESOURCE', 'By Resource']] as [BookingView, string][]).map(([v, label]) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => setView(v)}
                            className={`px-3 py-1.5 font-medium transition-colors ${view === v ? 'bg-violet-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            data-testid={`BTN_pm_booking_view_${v}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <Button
                    variant="fill"
                    icon={Plus}
                    type="button"
                    onClick={openAdd}
                    data-testid="BTN_pm_actual_add_open"
                >
                    Add Booking
                </Button>
            </div>

            {/* Listing */}
            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    {view === 'LIST' ? (
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Resource</th>
                                    <th style={{ textAlign: 'center' }}>Type</th>
                                    <th style={{ textAlign: 'right' }}>Hours</th>
                                    <th style={{ textAlign: 'right' }}>Rate</th>
                                    <th style={{ textAlign: 'right' }}>Cost</th>
                                    <th style={{ textAlign: 'center', width: '60px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <Skeleton.TableRows cols={['sm', 'sm', 'sm', 'sm', 'sm', 'sm']} rows={5} />
                                ) : chronological.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                            No actuals booked.
                                        </td>
                                    </tr>
                                ) : chronological.map(act => (
                                    <tr key={act.id}>
                                        <td>{act.actual_date}</td>
                                        <td>{act.resource_name}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={typeBadge(act.resource_type)}>{typeLabel(act.resource_type)}</span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{act.actual_hours}</td>
                                        <td style={{ textAlign: 'right' }}>${act.rate}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>${act.total_cost.toFixed(2)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteActual(act.id)}
                                                className="table-list-action table-list-action--delete"
                                                title="Remove booking"
                                                data-testid={`BTN_pm_actual_row_${act.id}_delete`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            {!loading && chronological.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Total Actual Cost:</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--interactive-primary-bg)' }}>${totalActual.toFixed(2)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : (
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th>Resource</th>
                                    <th style={{ textAlign: 'center' }}>Type</th>
                                    <th style={{ textAlign: 'right' }}>Budget (h)</th>
                                    <th style={{ textAlign: 'right' }}>Budget $</th>
                                    <th style={{ textAlign: 'right' }}>Actual (h)</th>
                                    <th style={{ textAlign: 'right' }}>Actual $</th>
                                    <th style={{ textAlign: 'right' }}>Variance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <Skeleton.TableRows cols={['sm', 'sm', 'sm', 'sm', 'sm', 'sm', 'sm']} rows={5} />
                                ) : aggregate.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                            No resources on this task.
                                        </td>
                                    </tr>
                                ) : aggregate.map(r => {
                                    const variance = r.budgetCost - r.actualCost;
                                    return (
                                        <tr key={r.key}>
                                            <td style={{ fontWeight: 500 }}>{r.name}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={typeBadge(r.type)}>{typeLabel(r.type)}</span>
                                            </td>
                                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.budgetHrs}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>${r.budgetCost.toFixed(2)}</td>
                                            <td style={{ textAlign: 'right' }}>{r.actualHrs}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>${r.actualCost.toFixed(2)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: variance >= 0 ? 'var(--success-text, #16a34a)' : 'var(--danger-text, #dc2626)' }}>
                                                {variance >= 0 ? '+' : ''}{variance.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {!loading && aggregate.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>Total:</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-secondary)' }}>${totalBudget.toFixed(2)}</td>
                                        <td />
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--interactive-primary-bg)' }}>${totalActual.toFixed(2)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: (totalBudget - totalActual) >= 0 ? 'var(--success-text, #16a34a)' : 'var(--danger-text, #dc2626)' }}>
                                            {(totalBudget - totalActual) >= 0 ? '+' : ''}{(totalBudget - totalActual).toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    )}
                </div>
            </div>

            {/* Add Booking modal — listing stays a pure listing. */}
            <Modal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} size="md" data-testid="MOD_pm_actual_add">
                <Modal.Header>New Booking</Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                        <FormControl.SearchSelect
                            label="Resource"
                            data-testid="SEL_pm_actual_resource"
                            options={resourceOptions}
                            value={newActual.resource_id ? `${newActual.resource_type}:${newActual.resource_id}` : null}
                            onChange={v => {
                                const [t, id] = (v ? String(v) : '').split(':');
                                setNewActual({ ...newActual, resource_type: (t || 'STAFF') as 'STAFF' | 'MEN' | 'MACHINE' | 'MATERIAL', resource_id: id ? Number(id) : 0 });
                            }}
                            placeholder={resourceOptions.length ? '-- Select Resource --' : 'No resources allocated to this task'}
                            clearable
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <ValidatedDatePicker
                                vh={vh} name="actual_date" code={305} required
                                label="Date"
                                value={newActual.actual_date}
                                onChange={e => setNewActual({ ...newActual, actual_date: e.target.value })}
                                data-testid="INP_pm_actual_date"
                            />
                            <ValidatedInput
                                vh={vh} name="actual_hours" code={206} required
                                label="Hours" type="number"
                                value={String(newActual.actual_hours)}
                                onChange={e => setNewActual({ ...newActual, actual_hours: parseFloat(e.target.value) })}
                                min="0" step="0.5"
                                data-testid="INP_pm_actual_hours"
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary-outline" type="button" onClick={() => setIsAddOpen(false)} data-testid="BTN_pm_actual_cancel">
                        Cancel
                    </Button>
                    <Button
                        variant="fill"
                        icon={Save}
                        type="button"
                        onClick={handleAddActual}
                        disabled={!newActual.resource_id || !newActual.actual_hours || booking}
                        data-testid="BTN_pm_actual_book"
                    >
                        {booking ? 'Booking...' : 'Book Actual'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

interface ActualBookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    task: Task;
}

const ActualBookingModal: React.FC<ActualBookingModalProps> = ({ isOpen, onClose, task }) => (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" data-testid="MOD_pm_actual_booking">
        <Modal.Header>Book Actuals</Modal.Header>
        <Modal.Body className="flex flex-col">
            {task && <ActualBookingPanel task={task} />}
        </Modal.Body>
    </Modal>
);

export default ActualBookingModal;
